import { initAxios } from '../../utils/axiosHelper';

export type ResultsReportReportType = 'summary' | 'full' | 'raw_data' | 'hits_only';

export type ResultsReportPaperSize = 'LETTER' | 'A4';

export type ResultsReportOrientation = 'portrait' | 'landscape';

export interface ResultsReportOptions {
  reportType: ResultsReportReportType;
  includeExecutiveSummary: boolean;
  includeScreeningResults: boolean;
  includeBusinessVerification: boolean;
  includeFullEvidence: boolean;
  topNResultsPerEntity: number;
  entityFilters?: {
    statusFlags?: string[];
    riskLevels?: string[];
    entityIds?: string[];
  };
  output: {
    format: 'pdf' | 'csv';
    paperSize: ResultsReportPaperSize;
    orientation: ResultsReportOrientation;
  };
  fileName?: string;
}

export type ResultsReportJobStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

export type ResultsReportStep =
  | 'queued'
  | 'collecting_entities'
  | 'adding_screening_results'
  | 'adding_business_verification'
  | 'generating_output'
  | 'completed';

export interface ResultsReportStatus {
  id: string;
  status: ResultsReportJobStatus;
  step?: ResultsReportStep;
  processedEntities?: number;
  totalEntities?: number;
  percent?: number;
  result?: {
    s3Key: string;
    fileName: string;
    entityCount: number;
    fileSizeBytes: number;
    generatedAt: string;
  };
  error?: string;
}

export interface ResultsReportStartResponse {
  jobId: string;
  reportId: string;
  status: string;
}

export interface ResultsReportDownloadUrl {
  url: string;
  expiresAt: string;
  fileName: string;
}

export interface ResultsReportRow {
  id: string;
  jobId: string;
  databaseId: string;
  groupId?: string;
  createdBy?: string;
  createdByName?: string;
  status: ResultsReportJobStatus;
  reportType?: string;
  options?: ResultsReportOptions;
  fileName?: string;
  entityCount?: number;
  fileSizeBytes?: number;
  errorMessage?: string;
  generatedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ResultsReportListPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ResultsReportListResponse {
  data: ResultsReportRow[];
  pagination: ResultsReportListPagination;
}

const extractMessage = (err: any, fallback: string): string => {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
};

export async function startResultsReport(payload: {
  databaseId: string;
  groupId: string;
  options: ResultsReportOptions;
}): Promise<ResultsReportStartResponse> {
  const instance = await initAxios();
  if (!instance) {
    throw new Error('Failed to initialize API client');
  }

  try {
    const resp = await instance.post(
      '/bite-db/results-reports/start',
      payload,
    );
    const data = resp.data || {};
    return {
      jobId: data.jobId,
      reportId: data.reportId,
      status: data.status,
    };
  } catch (err: any) {
    throw new Error(extractMessage(err, 'Failed to start results report'));
  }
}

export async function getResultsReportStatus(
  jobId: string,
): Promise<ResultsReportStatus> {
  const instance = await initAxios();
  if (!instance) {
    throw new Error('Failed to initialize API client');
  }

  try {
    const resp = await instance.get(
      `/bite-db/results-reports/${jobId}/status`,
    );
    return resp.data as ResultsReportStatus;
  } catch (err: any) {
    throw new Error(
      extractMessage(err, 'Failed to fetch results report status'),
    );
  }
}

export async function getResultsReportDownloadUrl(
  jobId: string,
  groupId: string,
): Promise<ResultsReportDownloadUrl> {
  const instance = await initAxios();
  if (!instance) {
    throw new Error('Failed to initialize API client');
  }

  try {
    const resp = await instance.get(
      `/bite-db/results-reports/${jobId}/download`,
      { params: { groupId: groupId } },
    );
    return resp.data as ResultsReportDownloadUrl;
  } catch (err: any) {
    throw new Error(
      extractMessage(err, 'Failed to fetch download URL'),
    );
  }
}

const normalizeResultsReportRow = (raw: any): ResultsReportRow | null => {
  const jobId = raw?.jobId || raw?.job_id;
  const databaseId = raw?.databaseId || raw?.database_id;
  const status = raw?.status as ResultsReportJobStatus | undefined;

  if (!jobId || !databaseId || !status) {
    return null;
  }

  const options =
    raw?.options && typeof raw.options === 'object'
      ? (raw.options as ResultsReportOptions)
      : undefined;

  const rawFileSize = raw?.fileSizeBytes ?? raw?.file_size_bytes;
  const normalizedFileSize =
    typeof rawFileSize === 'number'
      ? rawFileSize
      : typeof rawFileSize === 'string' && rawFileSize.trim()
      ? Number(rawFileSize)
      : undefined;

  return {
    id: raw?.id || jobId,
    jobId: jobId,
    databaseId: databaseId,
    groupId: raw?.groupId || raw?.group_id,
    createdBy: raw?.createdBy || raw?.created_by,
    createdByName: raw?.createdByName || raw?.created_by_name,
    status: status,
    reportType: raw?.reportType || raw?.report_type,
    options: options,
    fileName:
      raw?.fileName ||
      raw?.file_name ||
      (typeof options?.fileName === 'string' ? options.fileName : undefined),
    entityCount: raw?.entityCount ?? raw?.entity_count,
    fileSizeBytes:
      typeof normalizedFileSize === 'number' &&
      Number.isFinite(normalizedFileSize)
        ? normalizedFileSize
        : undefined,
    errorMessage: raw?.errorMessage || raw?.error_message,
    generatedAt:
      raw?.generatedAt ||
      raw?.completedAt ||
      raw?.completed_at ||
      raw?.updatedAt ||
      raw?.updated_at,
    createdAt: raw?.createdAt || raw?.created_at,
    updatedAt: raw?.updatedAt || raw?.updated_at,
  };
};

export async function listResultsReportsForDatabase(
  databaseId: string,
  groupId: string,
  options?: { page?: number; limit?: number },
): Promise<ResultsReportListResponse> {
  const instance = await initAxios();
  if (!instance) {
    return {
      data: [],
      pagination: {
        currentPage: options?.page || 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: options?.limit || 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }

  try {
    const requestedPage = options?.page || 1;
    const requestedLimit = options?.limit || 10;
    const resp = await instance.get(
      `/bite-db/databases/${databaseId}/results-reports`,
      { params: { groupId: groupId, page: requestedPage, limit: requestedLimit } },
    );
    const data = resp.data;
    const rawRows = Array.isArray(data) ? data : data?.data;
    const rows = (Array.isArray(rawRows) ? rawRows : [])
      .map((row) => normalizeResultsReportRow(row))
      .filter((row): row is ResultsReportRow => Boolean(row));

    const rawPagination = data?.pagination;
    const pagination: ResultsReportListPagination = {
      currentPage:
        typeof rawPagination?.currentPage === 'number'
          ? rawPagination.currentPage
          : requestedPage,
      totalPages:
        typeof rawPagination?.totalPages === 'number'
          ? rawPagination.totalPages
          : 1,
      totalItems:
        typeof rawPagination?.totalItems === 'number'
          ? rawPagination.totalItems
          : rows.length,
      itemsPerPage:
        typeof rawPagination?.itemsPerPage === 'number'
          ? rawPagination.itemsPerPage
          : requestedLimit,
      hasNextPage:
        typeof rawPagination?.hasNextPage === 'boolean'
          ? rawPagination.hasNextPage
          : false,
      hasPreviousPage:
        typeof rawPagination?.hasPreviousPage === 'boolean'
          ? rawPagination.hasPreviousPage
          : false,
    };

    return { data: rows, pagination: pagination };
  } catch (err: any) {
    return {
      data: [],
      pagination: {
        currentPage: options?.page || 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: options?.limit || 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
}
